import { Order } from './components/table/TableHead';
import Device from './models/device';
import { DeviceStore } from './pages/devices/deviceContext';
import { Log } from './pages/logs/logsContext';

// Manual formatting: toLocaleDateString/toLocaleTimeString build an Intl formatter
// per call, which made filtering ~16K logs (4 date strings per row) crawl.
export function formatDateTime(datetime: string): string {
    const date = new Date(datetime);
    if (isNaN(date.getTime())) return 'Invalid Date, Invalid Date';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}, ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function formatDateTimeUTC(datetime: string): string {
    const date = new Date(datetime);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
}

export function filterDevices(devices: DeviceStore[] | Device[], filter?: string): DeviceStore[] | Device[] {
    if (!filter) return devices;

    const { include, exclude, exact, excludeExact } = getFilters(filter);

    if (include.length === 0 && exclude.length === 0 && exact.length === 0 && excludeExact.length === 0) return devices;

    return devices.filter(device => {
        // Create a searchable text string from all device properties
        const deviceValues = Object.values(device)
            .filter(val => val !== null && val !== undefined)
            .map(val => String(val).toLowerCase());

        // Add the model and memory_size together as they appear in the UI
        const modelWithMemory = `${device.model || ''} ${device.memory_size ? `${device.memory_size}GB` : ''}`.toLowerCase();
        deviceValues.push(modelWithMemory);

        const deviceText = deviceValues.join(' ');

        const matchesIncludes = include.length === 0 ||
            include.every(term => deviceText.includes(term));

        const matchesExcludes = exclude.length === 0 ||
            exclude.every(term => !deviceText.includes(term));

        const matchesExact = exact.length === 0 ||
            exact.every(term => deviceText.includes(term));

        const matchesExcludeExact = excludeExact.length === 0 ||
            excludeExact.every(term => !deviceText.includes(term));

        return matchesIncludes && matchesExcludes && matchesExact && matchesExcludeExact;
    });
}

// Dotted numeric version like "3.10.2" — compare segment by segment, not lexically
const versionRegex = /^\d+(\.\d+)+$/;

function compareVersions(a: string, b: string): number {
    const segA = a.split('.').map(Number);
    const segB = b.split('.').map(Number);
    const len = Math.max(segA.length, segB.length);
    for (let i = 0; i < len; i++) {
        const diff = (segB[i] ?? 0) - (segA[i] ?? 0);
        if (diff !== 0) return diff;
    }
    return 0;
}

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (a[orderBy] === undefined || a[orderBy] === null || a[orderBy] === '')
        return 1;
    if (b[orderBy] === undefined || b[orderBy] === null || b[orderBy] === '')
        return -1;

    if (typeof a[orderBy] === 'string' && typeof b[orderBy] === 'string' &&
        versionRegex.test(a[orderBy]) && versionRegex.test(b[orderBy])) {
        return compareVersions(a[orderBy], b[orderBy]);
    }

    if (typeof a[orderBy] === 'string' && !isNaN(Number(a[orderBy])) &&
        typeof b[orderBy] === 'string' && !isNaN(Number(b[orderBy]))) {
        return Number(b[orderBy]) - Number(a[orderBy]);
    }

    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

export function getComparator<Key extends keyof Device>(order: Order, orderBy: Key): (
    a: Partial<Record<Key, number | string | boolean | object | undefined>>,
    b: Partial<Record<Key, number | string | boolean | object | undefined>>,
) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function isValidMacAddress(mac: string): boolean {
    return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(mac);
}

export function isLastHeartbeatStale(lastHeartbeat?: string, fetch = (new Date()).toISOString()): boolean {
    if (!lastHeartbeat)
        return true;

    const lastHeartbeatTime = (new Date(lastHeartbeat)).getTime();
    const fetchTime = (new Date(fetch)).getTime();

    return ((fetchTime - lastHeartbeatTime) / 1000) > 90;
}

export function getFilters(filter?: string): { include: string[], exclude: string[], exact: string[], excludeExact: string[] } {
    if (!filter) return { include: [], exclude: [], exact: [], excludeExact: [] };

    // Support backtick-enclosed exact terms, quoted phrases, and exclusion with - prefix
    const termRegex = /(-?`[^`]+`|-?"[^"]+"|\S+)/g;
    const terms: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = termRegex.exec(filter)) !== null) {
        terms.push(match[0]);
    }

    const exact = terms
        .filter(term => term.startsWith('`') && term.endsWith('`'))
        .map(term => term.slice(1, -1).toLowerCase());

    const excludeExact = terms
        .filter(term => term.startsWith('-`') && term.endsWith('`'))
        .map(term => term.slice(2, -1).toLowerCase());

    const nonExactTerms = terms.filter(term => !term.endsWith('`') || (!term.startsWith('`') && !term.startsWith('-`')));

    const include = nonExactTerms
        .filter(term => !(term.startsWith('-"') && term.endsWith('"')) && !term.startsWith('-'))
        .map(term => term.replace(/^"|"$/g, '').toLowerCase());

    const exclude = nonExactTerms
        .filter(term => (term.startsWith('-"') && term.endsWith('"')) || (term.startsWith('-') && !term.startsWith('-"')))
        .map(term => {
            if (term.startsWith('-"') && term.endsWith('"')) {
                return term.slice(2, -1).toLowerCase();
            }
            return term.slice(1).toLowerCase();
        });

    return { include, exclude, exact, excludeExact };
}